/**
 * FantasyDnaPage — Manager Archetype Profile (v2 redesign)
 *
 * Dark archetype hero (mascot + archetype name), trait profile bars,
 * tendency chips, season history, draft style, and JAI's personality read.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page, V2Card, V2Bar, JaiSummaryCard } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_DNA = {
  archetype: 'The Contrarian',
  archetypeDesc: 'You zag when everyone zigs. Your draft board ignores conventional wisdom, and somehow it keeps working.',

  seasons: [
    { year: 2023, record: '9-5',  finish: 2,    champWin: false, avgPts: 168.4 },
    { year: 2024, record: '11-3', finish: 1,    champWin: true,  avgPts: 179.2 },
    { year: 2025, record: '10-4', finish: null, champWin: null,  avgPts: 184.6 },
  ],

  tendencies: [
    { label: 'Waiver activity',     value: 88, descriptor: 'Hyper-active' },
    { label: 'Trade aggression',    value: 72, descriptor: 'Aggressive' },
    { label: 'Risk tolerance',      value: 91, descriptor: 'High risk' },
    { label: 'Flexibility',         value: 65, descriptor: 'Adaptable' },
    { label: 'Start/sit accuracy',  value: 54, descriptor: 'Average' },
    { label: 'Injury awareness',    value: 40, descriptor: 'Low' },
  ],

  chips: ['Drafts wings early', 'Punts the C slot', 'Hyper-active on waivers', 'Trades at deadlines'],

  draftProfile: {
    avgPickByPosition: [
      { pos: 'PG', avgRound: 2.1, leagueAvg: 2.8 },
      { pos: 'SG', avgRound: 3.4, leagueAvg: 3.2 },
      { pos: 'SF', avgRound: 1.8, leagueAvg: 2.5 },
      { pos: 'PF', avgRound: 4.1, leagueAvg: 3.8 },
      { pos: 'C',  avgRound: 5.5, leagueAvg: 4.2 },
    ],
    avoidedPositions: 'Centers — you historically deprioritise the C slot and make up value on the wire.',
  },

  jaiRead: "You are a contrarian's contrarian. Where the consensus runs towards safe, proven commodities, you're hunting sleepers, breakout candidates, and undervalued assets. Your 2024 championship wasn't luck — it was the product of a genuinely different process. The risk? You occasionally miss on injury-prone players that a more conservative manager would have passed on. Your Achilles heel is the injury radar: you're 40th percentile on spotting health risk before it hits your lineup.",

  funFacts: [
    'You\'ve made 23 trades across 3 seasons — 2.4× the league average.',
    'Your waiver wire pickups have outscored 78% of other managers\' pickups.',
    'You\'ve started a player who scored 0 points exactly 7 times — 3× in 2023.',
    'Your highest single-week score is 231.4 — set in Week 9, 2024.',
  ],
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function FantasyDnaPage() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? { name: 'The Hardwood Cartel' } : null)
  const [loading, setLoading] = useState(!IS_PREVIEW)
  const dna = PREVIEW_DNA

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <V2Page
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Manager profile` : 'Manager profile'}
          </Link>
        }
        title="Fantasy DNA"
        sub="How you actually manage — measured from 3 seasons of moves."
        maxWidth={780}
      >

        {/* ── Dark archetype hero ───────────────────────────────────────── */}
        <div style={{ background: V2.darkHero, borderRadius: 18, padding: '24px 28px', color: 'white', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 22, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: -60, top: -60, width: 240, height: 240, borderRadius: '50%', background: 'rgba(58,124,255,0.1)' }} />
          <div style={{ flexShrink: 0, position: 'relative' }}>
            <JAIMascot size={62} state="idle" animate={true} />
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', color: V2.blueBright, fontFamily: JAI_FONTS.mono, marginBottom: 6 }}>
              YOUR ARCHETYPE
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 }}>{dna.archetype}</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 7, lineHeight: 1.5, maxWidth: 440 }}>
              {dna.archetypeDesc}
            </div>
          </div>
        </div>

        {/* ── Trait profile + tendency chips ────────────────────────────── */}
        <V2Card pad="18px 22px" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, marginBottom: 16 }}>Trait profile</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {dna.tendencies.map(t => (
              <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ width: 130, fontSize: 12.5, fontWeight: 600, color: V2.navy, flexShrink: 0 }}>{t.label}</span>
                <V2Bar value={t.value} tone={t.value >= 60 ? 'strong' : 'rgba(30,91,214,0.35)'} height={8} style={{ flex: 1 }} />
                <span style={{ width: 30, fontSize: 12.5, fontWeight: 800, color: t.value >= 60 ? V2.blue : V2.mute, fontFamily: JAI_FONTS.mono, textAlign: 'right', flexShrink: 0 }}>{t.value}</span>
                <span style={{ width: 86, fontSize: 11.5, color: V2.faint, textAlign: 'right', flexShrink: 0 }}>{t.descriptor}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
            {dna.chips.map(c => (
              <span key={c} style={{ fontSize: 12, fontWeight: 600, color: V2.blue, border: '1px solid rgba(10,31,68,0.18)', borderRadius: 999, padding: '5px 13px' }}>
                {c}
              </span>
            ))}
          </div>
        </V2Card>

        {/* ── Season history + draft style ──────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <V2Card pad="18px 20px">
            <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, marginBottom: 12 }}>Season history</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {dna.seasons.map(s => {
                const active = s.finish === null
                return (
                  <div key={s.year} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 10,
                    background: V2.paper,
                    border: `1px solid ${active ? V2.blue : V2.hairSoft}`,
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 36 }}>{s.year}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, fontFamily: JAI_FONTS.mono, flex: 1 }}>{s.record}</span>
                    <span style={{ fontSize: 11.5, color: V2.mute }}>{s.finish ? `#${s.finish}` : 'Live'}</span>
                    {s.champWin && (
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: V2.warning, padding: 0, letterSpacing: '0.08em' }}>
                        CHAMPION
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </V2Card>

          <V2Card pad="18px 20px">
            <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, marginBottom: 12 }}>Draft style</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {dna.draftProfile.avgPickByPosition.map(p => (
                <div key={p.pos} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 28, fontSize: 11, fontWeight: 800, color: V2.navy, fontFamily: JAI_FONTS.mono }}>{p.pos}</span>
                  <V2Bar
                    value={100 - (p.avgRound / 8) * 100}
                    tone={p.avgRound <= p.leagueAvg ? 'strong' : 'weak'}
                    height={5}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 11, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 56, textAlign: 'right' }}>Rd {p.avgRound.toFixed(1)}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: V2.mute, lineHeight: 1.55, margin: '12px 0 0', paddingTop: 11, borderTop: '1px solid rgba(10,31,68,0.06)' }}>
              {dna.draftProfile.avoidedPositions}
            </p>
          </V2Card>
        </div>

        {/* ── JAI's read ────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <JaiSummaryCard headline="JAI's read on you.">
            {dna.jaiRead}
          </JaiSummaryCard>
        </div>

        {/* ── By the numbers ────────────────────────────────────────────── */}
        <V2Card pad="18px 20px">
          <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, marginBottom: 12 }}>By the numbers</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {dna.funFacts.map((fact, i) => (
              <div key={i} style={{ display: 'flex', gap: 10 }}>
                <span style={{ color: V2.blue, fontWeight: 800, flexShrink: 0 }}>·</span>
                <span style={{ color: 'rgba(10,31,68,0.78)', fontSize: 13.5, lineHeight: 1.55 }}>{fact}</span>
              </div>
            ))}
          </div>
        </V2Card>

      </V2Page>
    </AppShell>
  )
}
